import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PaginatedResult,
  PurchaseOrderDetail,
  PurchaseOrderStatus,
  PurchaseOrderSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';
import { inventoryKeys } from '@/modules/inventory/api/inventory.api';

export interface PurchaseOrderListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  supplierId?: string;
  branchId?: string;
  status?: PurchaseOrderStatus | '';
  dateFrom?: string;
  dateTo?: string;
}

/** One line as a person entered it. The server prices it; nothing here is a total. */
export interface PurchaseOrderLineInput {
  variantId: string;
  quantity: number;
  agreedUnitCostMinor: number;
  taxProfileId?: string;
}

export interface PurchaseOrderHeaderInput {
  expectedDeliveryDate?: string;
  supplierReference?: string;
  notes?: string;
}

export const purchaseOrderKeys = {
  all: ['purchase-orders'] as const,
  list: (params: PurchaseOrderListParams) => [...purchaseOrderKeys.all, 'list', params] as const,
  detail: (id: string) => [...purchaseOrderKeys.all, 'detail', id] as const,
};

export function usePurchaseOrdersQuery(params: PurchaseOrderListParams) {
  return useQuery({
    queryKey: purchaseOrderKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<PurchaseOrderSummary>>('/purchasing/orders', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
          ...(params.supplierId ? { supplierId: params.supplierId } : {}),
          ...(params.branchId ? { branchId: params.branchId } : {}),
          ...(params.status ? { status: params.status } : {}),
          ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params.dateTo ? { dateTo: params.dateTo } : {}),
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function usePurchaseOrderQuery(id: string | undefined) {
  return useQuery({
    queryKey: purchaseOrderKeys.detail(id ?? ''),
    queryFn: () => api.get<PurchaseOrderDetail>(`/purchasing/orders/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

/**
 * A purchase order that has been received against has changed stock, so the inventory views go
 * stale with it. Invalidating both keys is cheaper than reasoning about which of them moved.
 */
function usePurchaseOrderInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
  };
}

/** Drafting allocates the PO- reference, so a double click must replay the one draft. */
export function useCreatePurchaseOrderMutation() {
  const invalidate = usePurchaseOrderInvalidation();

  return useIdempotentMutation<
    PurchaseOrderDetail,
    PurchaseOrderHeaderInput & { supplierId: string; lines?: PurchaseOrderLineInput[] }
  >((payload, config) => api.post<PurchaseOrderDetail>('/purchasing/orders', payload, config), {
    onSuccess: invalidate,
  });
}

/**
 * Saves a change at the version the screen showed.
 *
 * `lines` replaces the whole set, which is how a line is removed; leaving it out leaves the lines
 * alone, which is what an ORDERED order's header edit does.
 */
export function useUpdatePurchaseOrderMutation() {
  const invalidate = usePurchaseOrderInvalidation();

  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: PurchaseOrderHeaderInput & {
      id: string;
      version: number;
      supplierId?: string;
      lines?: PurchaseOrderLineInput[];
    }) => api.patch<PurchaseOrderDetail>(`/purchasing/orders/${id}`, payload),
    onSuccess: invalidate,
  });
}

/** Placing is the commitment: one key per attempt, so a double click commits the branch once. */
export function usePlacePurchaseOrderMutation() {
  const invalidate = usePurchaseOrderInvalidation();

  return useIdempotentMutation<PurchaseOrderDetail, { id: string; version: number }>(
    (payload, config) =>
      api.post<PurchaseOrderDetail>(
        `/purchasing/orders/${payload.id}/order`,
        { version: payload.version },
        config,
      ),
    { onSuccess: invalidate },
  );
}

export function useCancelPurchaseOrderMutation() {
  const invalidate = usePurchaseOrderInvalidation();

  return useIdempotentMutation<
    PurchaseOrderDetail,
    { id: string; version: number; reason: string }
  >(
    (payload, config) =>
      api.post<PurchaseOrderDetail>(
        `/purchasing/orders/${payload.id}/cancel`,
        { version: payload.version, reason: payload.reason },
        config,
      ),
    { onSuccess: invalidate },
  );
}

/** Gives up on what the supplier still owes. Nothing already received is touched. */
export function useClosePurchaseOrderMutation() {
  const invalidate = usePurchaseOrderInvalidation();

  return useIdempotentMutation<
    PurchaseOrderDetail,
    { id: string; version: number; reason: string }
  >(
    (payload, config) =>
      api.post<PurchaseOrderDetail>(
        `/purchasing/orders/${payload.id}/close`,
        { version: payload.version, reason: payload.reason },
        config,
      ),
    { onSuccess: invalidate },
  );
}
